import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  GitCompareArrows,
  LayoutDashboard,
  ListTree,
  Rows3,
  Search,
  Target,
} from 'lucide-react';
import { EChart, type ChartOption } from './charts/EChart';
import { loadReviewBundle } from './data/client';
import { countFeasibleCases } from './data/coverage';
import type {
  CaseResult,
  Distribution,
  FamilyCheckpoint,
  ResultFamily,
  ReviewBundle,
  ReviewRun,
  TrajectoryPoint,
} from './data/types';

type View = 'overview' | 'results' | 'runs';

export function App() {
  const [bundle, setBundle] = useState<ReviewBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('overview');
  const [selectedCaseId, setSelectedCaseId] = useState('');

  useEffect(() => {
    loadReviewBundle()
      .then((loaded) => {
        setBundle(loaded);
        setSelectedCaseId(loaded.results.families[0]?.cases[0]?.case_id ?? '');
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : String(reason)));
  }, []);

  if (error) return <StatePage title="Review Bundle 加载失败" detail={error} error />;
  if (!bundle) return <StatePage title="正在校验评审数据" detail="" />;

  return (
    <main className="app-shell">
      <ReviewHeader bundle={bundle} />
      <ViewTabs view={view} onChange={setView} />
      <div className="page-content">
        {view === 'overview' ? (
          <OverviewView
            bundle={bundle}
            onOpenResults={(caseId) => {
              setSelectedCaseId(caseId);
              setView('results');
            }}
          />
        ) : view === 'results' ? (
          <ResultsView
            bundle={bundle}
            selectedCaseId={selectedCaseId}
            onSelectCase={setSelectedCaseId}
          />
        ) : (
          <RunsView bundle={bundle} />
        )}
      </div>
    </main>
  );
}

function ReviewHeader({ bundle }: { bundle: ReviewBundle }) {
  const { overview } = bundle;
  const comparison = overview.render_mode === 'comparison';
  const config = overview.preset.configuration;
  return (
    <header className="review-header">
      <div className="brand-row">
        <div className="brand-lockup">
          <span className="brand-mark">OA</span>
          <div><strong>OptAgent 策略评审</strong><span>BENCHMARK EVIDENCE</span></div>
        </div>
        <div className="strategy-identity">
          <div className="strategy-label-row">
            <span>{comparison ? '当前策略 / Baseline' : '当前策略'}</span>
            <span className={`mode-tag ${comparison ? 'comparison' : ''}`}>
              {comparison ? <GitCompareArrows size={12} /> : <CircleDot size={12} />}
              {comparison ? '固定基线比较' : '单策略结果'}
            </span>
          </div>
          <strong>
            {displayStrategy(overview.current.strategy)}
            {comparison && overview.baseline ? <><i>/</i>{displayStrategy(overview.baseline.strategy)}</> : null}
          </strong>
        </div>
        <div className="header-context">
          <span><b>目标</b>{overview.target_families.map(familyLabel).join('、') || '未声明'}</span>
          <span>{overview.preset.id} v{overview.preset.version} · {overview.case_count}×{overview.preset.expected_seed_count}</span>
        </div>
        <div className="preset-config" aria-label="评测预设配置">
          <ConfigFact label="时限" value={formatConfigWindow(config.time_limit_s, config.time_limit_range_s)} />
          <ConfigFact label="迭代" value={config.max_iterations == null ? '不限' : String(config.max_iterations)} />
          <ConfigFact label="种群" value={formatConfigNumber(config.population_size)} />
          <ConfigFact label="线程" value={formatConfigNumber(config.thread_count)} />
          <ConfigFact label="采样" value={formatConfigSeconds(config.observation_interval_s)} />
          <ConfigFact label="检查点" value={overview.preset.formal_checkpoints_s.map(formatCompactSeconds).join('·')} />
        </div>
        <div className="header-meta">
          <span>Current {shortCommit(overview.current.optagent_commit)}</span>
          {overview.baseline && <span>Baseline {shortCommit(overview.baseline.optagent_commit)}</span>}
          <span>协议 {overview.protocol_id}</span>
        </div>
      </div>
    </header>
  );
}

function ViewTabs({ view, onChange }: { view: View; onChange: (view: View) => void }) {
  const tabs: Array<{ id: View; label: string; icon: React.ReactNode }> = [
    { id: 'overview', label: '概览', icon: <LayoutDashboard size={16} /> },
    { id: 'results', label: '结果明细', icon: <ListTree size={16} /> },
    { id: 'runs', label: '运行记录', icon: <Rows3 size={16} /> },
  ];
  return (
    <nav className="view-tabs" aria-label="评审视图">
      {tabs.map((tab) => (
        <button key={tab.id} type="button" className={view === tab.id ? 'active' : ''} onClick={() => onChange(tab.id)}>
          {tab.icon}{tab.label}
        </button>
      ))}
    </nav>
  );
}

function OverviewView({ bundle, onOpenResults }: { bundle: ReviewBundle; onOpenResults: (caseId: string) => void }) {
  const { overview } = bundle;
  const comparison = overview.render_mode === 'comparison';
  return (
    <>
      <section className="section-block">
        <SectionTitle title="算例族检查点" detail="各算例族独立展示，不汇总为总分" />
        <div className={`reading-guide ${comparison ? 'comparison' : 'single'}`}>
          <GuideItem title="Reference Gap" text="目标值相对 reference 的差距，越低越好；负值表示优于 reference。" />
          {comparison && <GuideItem title="变化（pp）" text="Current Gap − Baseline Gap；负值改善，正值退化，0 不变。" />}
          <GuideItem title="可行覆盖" text="检查点已有可行 incumbent 的 case 数 / 该算例族 case 总数。" />
          <GuideItem title="P25–P75（跨 case）" text={comparison ? '这里统计各 case 的变化值，反映算例间差异；seed 稳定性仅在结果明细页选中单个 case 后的逐秒解质量图中按不同 seed 展示。范围越窄，case 间结果越集中。' : '这里统计各 case 的 Median Gap，反映算例间差异；seed 稳定性仅在结果明细页选中单个 case 后的逐秒解质量图中按不同 seed 展示。范围越窄，case 间结果越集中。'} />
        </div>
        <div className="family-grid">
          {overview.family_checkpoints.map((family) => (
            <FamilyPanel
              key={family.family}
              family={family}
              cases={bundle.results.families.find((item) => item.family === family.family)?.cases ?? []}
              comparison={comparison}
              onOpen={() => onOpenResults(bundle.results.families.find((item) => item.family === family.family)?.cases[0]?.case_id ?? '')}
            />
          ))}
        </div>
      </section>
    </>
  );
}

function FamilyPanel({
  family,
  cases,
  comparison,
  onOpen,
}: {
  family: ReviewBundle['overview']['family_checkpoints'][number];
  cases: CaseResult[];
  comparison: boolean;
  onOpen: () => void;
}) {
  return (
    <article className={`family-panel ${family.role}`}>
      <header>
        <div>
          <span className="family-role">{family.role === 'target' ? <Target size={13} /> : <CircleDot size={12} />}{roleLabel(family.role)}</span>
          <FamilyName family={family.family} />
        </div>
        <button type="button" title="打开结果明细" onClick={onOpen}><ChevronRight size={18} /></button>
      </header>
      <div className="checkpoint-grid header"><span>时间</span><span>{comparison ? '变化' : 'Median Gap'}</span><span>P25–P75</span><span>可行覆盖</span></div>
      {family.checkpoints.map((point) => {
        const delta = point.case_delta_pp.median;
        const spread = comparison ? point.case_delta_pp : point.current.gap;
        return (
          <div className="checkpoint-grid" key={point.time_s}>
            <strong>{formatSeconds(point.time_s)}</strong>
            {comparison ? <ChangeValue value={delta} /> : <b>{formatPercent(point.current.gap.median)}</b>}
            <span>{formatRange(spread)}</span>
            <span>{countFeasibleCases(cases, point.time_s)} / {family.case_count}</span>
          </div>
        );
      })}
      <footer>{family.case_count} 个算例</footer>
    </article>
  );
}

function ResultsView({
  bundle,
  selectedCaseId,
  onSelectCase,
}: {
  bundle: ReviewBundle;
  selectedCaseId: string;
  onSelectCase: (caseId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(
    () => new Set(bundle.results.families.map((family) => family.family)),
  );
  const selected = findCase(bundle.results.families, selectedCaseId) ?? bundle.results.families[0]?.cases[0];
  const filtered = useMemo(() => filterFamilies(bundle.results.families, query), [bundle.results.families, query]);
  useEffect(() => {
    const familyId = findCase(bundle.results.families, selectedCaseId)?.family;
    if (!familyId) return;
    setExpandedFamilies((current) => current.has(familyId) ? current : new Set(current).add(familyId));
  }, [bundle.results.families, selectedCaseId]);
  useEffect(() => {
    const caseButton = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-case-id]'))
      .find((element) => element.dataset.caseId === selectedCaseId);
    caseButton?.scrollIntoView({ block: 'nearest' });
  }, [expandedFamilies, filtered, selectedCaseId]);
  const toggleFamily = (familyId: string) => {
    setExpandedFamilies((current) => {
      const next = new Set(current);
      if (next.has(familyId)) next.delete(familyId);
      else next.add(familyId);
      return next;
    });
  };
  if (!selected) return <StatePage title="没有 case 证据" detail="Bundle 中未包含可展示的 case。" />;
  return (
    <section className="results-layout">
      <aside className="case-browser">
        <label className="tree-search"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索算例族或 case" /></label>
        <p className="browser-help">case 右侧数字为有效运行 / 预期 seed 数。</p>
        <div className="tree-scroll">
          {filtered.map((family) => (
            <div className="tree-family" key={family.family}>
              <button
                type="button"
                className="tree-family-label"
                aria-expanded={expandedFamilies.has(family.family)}
                onClick={() => toggleFamily(family.family)}
              >
                {expandedFamilies.has(family.family) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span>{familyLabel(family.family)}</span><small>{family.cases.length}</small>
              </button>
              {expandedFamilies.has(family.family) && family.cases.map((item) => (
                <button key={item.case_id} data-case-id={item.case_id} type="button" className={selected.case_id === item.case_id ? 'selected' : ''} onClick={() => onSelectCase(item.case_id)}>
                  <span>{item.case_id}</span><small>{item.valid_run_count}/{item.expected_run_count}</small>
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && <p className="empty-tree">没有匹配结果</p>}
        </div>
      </aside>
      <CaseDetail bundle={bundle} item={selected} />
    </section>
  );
}

function CaseDetail({ bundle, item }: { bundle: ReviewBundle; item: CaseResult }) {
  const comparison = bundle.overview.render_mode === 'comparison';
  const checkpointDetail = item.checkpoints.map((point) => formatSeconds(point.time_s)).join('、') || '无检查点';
  const qualityChart = useMemo(() => qualityOption(item, comparison), [item, comparison]);
  const runs = bundle.runs.filter((run) => run.case_id === item.case_id);
  return (
    <article className="case-detail">
      <header className="case-heading">
        <div><span>{familyLabel(item.family)}</span><h1>{item.case_id}</h1></div>
        <dl><div><dt>评审角色</dt><dd>{roleLabel(findFamily(bundle.results.families, item.family)?.role ?? 'observation')}</dd></div><div><dt>目标方向</dt><dd>{senseLabel(item.objective_sense)}</dd></div><div><dt>Reference</dt><dd>{formatNumber(item.reference_objective)}</dd></div><div><dt>有效运行</dt><dd>{item.valid_run_count} / {item.expected_run_count}</dd></div></dl>
      </header>

      <section className="case-section">
        <SectionTitle title="正式检查点" detail={`${checkpointDetail} 来自同一次搜索轨迹`} />
        <div className="table-wrap"><CheckpointTable points={item.checkpoints} comparison={comparison} reference={item.reference_objective} sense={item.objective_sense} /></div>
        <p className="inline-explanation">{comparison ? 'Gap 仅在有可行 incumbent 时计算。比较值使用同一时刻双方都可行的配对 seed；不可行 seed 只进入可行覆盖，不会转换成惩罚 Gap。Objective 显示 Median，Δ Reference 按目标方向计算，正值表示退化，负值表示改善。' : 'Gap 仅使用该时刻已有可行 incumbent 且快照验证通过的 seed 计算；不可行 seed 只进入可行覆盖，不会转换成惩罚 Gap。Objective 显示 Median，Δ Reference 按目标方向计算，正值表示退化，负值表示改善。'}</p>
      </section>

      <section className="case-section charts-stack">
        <SectionTitle title="逐秒解质量" detail="实线为 Gap Median，悬停查看 Objective" />
        <div className="chart-legend"><span><i className="current-line" />Current</span>{comparison && <span><i className="baseline-line" />Baseline</span>}<b>曲线越低越好</b></div>
        <div className="chart-frame"><EChart option={qualityChart} label={`${item.case_id} 逐秒 Reference Gap 与 Objective 曲线`} /></div>
        <p className="inline-explanation">每秒只读取该时刻之前已经成为 incumbent 的解。这里的 P25–P75 统计同一 case 不同 seed 的结果，范围越窄表示 seed 间越稳定；实线为 Gap Median，悬停可同时查看 Gap 与实际 Objective 的 Median、P25–P75。图中样本量可能随可行解出现而变化。</p>
      </section>

      <section className="case-section">
        <SectionTitle title="Seed 证据" detail={`${runs.length} 个运行坐标`} />
        <div className="table-wrap"><SeedTable rows={runs} comparison={comparison} /></div>
        <p className="inline-explanation">“有效”表示该 seed 的全部观测快照均通过独立验证；退出列使用搜索实际结束时间。外部取消、基础设施中断或任一快照验证失败会使该运行无效。</p>
      </section>
    </article>
  );
}

function CheckpointTable({
  points,
  comparison,
  reference,
  sense,
}: {
  points: TrajectoryPoint[];
  comparison: boolean;
  reference: number | null;
  sense: string;
}) {
  return (
    <table className="checkpoint-table"><thead><tr><th>检查点</th><th>Current 可行</th>{comparison && <th>Baseline 可行</th>}<th>Current Gap</th>{comparison && <><th>Baseline Gap</th><th>变化</th></>}<th>Current Objective / Δ Reference</th>{comparison && <th>Baseline Objective / Δ Reference</th>}</tr></thead>
      <tbody>{points.map((point) => <tr key={point.time_s}><td><strong>{formatSeconds(point.time_s)}</strong></td><td>{point.current.feasible} / {point.current.valid}</td>{comparison && <td>{point.baseline?.feasible ?? 0} / {point.baseline?.valid ?? 0}</td>}<td>{formatPercent(point.paired_gap?.current_median ?? point.current.gap.median)}</td>{comparison && <><td>{formatPercent(point.paired_gap?.baseline_median ?? null)}</td><td><ChangeValue value={point.paired_gap?.delta_pp ?? null} /></td></>}<td><ObjectiveValue value={point.current.objective.median} reference={reference} sense={sense} /></td>{comparison && <td><ObjectiveValue value={point.baseline?.objective.median ?? null} reference={reference} sense={sense} /></td>}</tr>)}</tbody>
    </table>
  );
}

function SeedTable({ rows, comparison }: { rows: ReviewRun[]; comparison: boolean }) {
  return (
    <table><thead><tr><th>Seed</th><th>Current</th>{comparison && <th>Baseline</th>}<th>Current 退出</th>{comparison && <th>Baseline 退出</th>}</tr></thead><tbody>
      {rows.map((row) => {
        const currentLast = lastItem(row.current.observations);
        const baselineLast = lastItem(row.baseline?.observations ?? []);
        return <tr key={row.seed}><td>{row.seed}</td><td><Verification passed={row.current.verification_passed} status={row.current.status} errors={row.current.verification_errors} /></td>{comparison && <td><Verification passed={Boolean(row.baseline?.verification_passed)} status={row.baseline?.status ?? 'missing'} errors={row.baseline?.verification_errors} /></td>}<td>{exitLabel(currentLast)}</td>{comparison && <td>{exitLabel(baselineLast)}</td>}</tr>;
      })}
    </tbody></table>
  );
}

function RunsView({ bundle }: { bundle: ReviewBundle }) {
  const [query, setQuery] = useState('');
  const rows = bundle.runs.filter((row) => !query || `${row.family} ${row.case_id} ${row.seed}`.toLowerCase().includes(query.toLowerCase()));
  const comparison = bundle.overview.render_mode === 'comparison';
  return <section className="section-block"><SectionTitle title="运行记录" detail="每行对应一个 case × seed 运行坐标" /><p className="inline-explanation records-help">{comparison ? 'Current / Baseline 显示各自运行的快照验证状态；目标值是最后观测快照经验证后的原始目标值，不是 Reference Gap。搜索仅过滤 Bundle 内已有记录，不改变对比对象。' : 'Current 显示该运行的快照验证状态；目标值是最后观测快照经验证后的原始目标值，不是 Reference Gap。搜索仅过滤 Bundle 内已有记录。'}</p><label className="run-search"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索 case、算例族或 seed" /></label><div className="table-wrap"><table><thead><tr><th>算例族</th><th>Case</th><th>Seed</th><th>Current</th>{comparison && <th>Baseline</th>}<th>Current 目标值</th></tr></thead><tbody>{rows.map((row) => <tr key={`${row.case_id}-${row.seed}`}><td>{familyLabel(row.family)}</td><td>{row.case_id}</td><td>{row.seed}</td><td><Verification passed={row.current.verification_passed} status={row.current.status} errors={row.current.verification_errors} /></td>{comparison && <td><Verification passed={Boolean(row.baseline?.verification_passed)} status={row.baseline?.status ?? 'missing'} errors={row.baseline?.verification_errors} /></td>}<td>{formatNumber(lastItem(row.current.observations)?.objective ?? null)}</td></tr>)}</tbody></table></div></section>;
}

function qualityOption(item: CaseResult, comparison: boolean): ChartOption {
  const x = item.trajectory.map((point) => `${point.time_s}s`);
  const current = item.trajectory.map((point) => comparison ? point.paired_gap?.current : point.current.gap);
  const baseline = item.trajectory.map((point) => point.paired_gap?.baseline);
  const series = [
    ...bandSeries('Current', current, '#087f5b', 'current'),
    ...(comparison ? bandSeries('Baseline', baseline, '#1864ab', 'baseline') : []),
  ];
  return {
    animation: false,
    grid: { left: 62, right: 24, top: 24, bottom: 42 },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => formatQualityTooltip(params, item, comparison),
    },
    xAxis: { type: 'category', data: x, axisTick: { show: false }, axisLabel: { interval: 1 } },
    yAxis: { type: 'value', name: 'Gap %', axisLabel: { formatter: '{value}%' }, splitLine: { lineStyle: { color: '#e6eaec' } } },
    series,
  } as ChartOption;
}

function formatQualityTooltip(params: unknown, item: CaseResult, comparison: boolean): string {
  const entries = Array.isArray(params) ? params : [params];
  const first = entries.find((entry) => isChartParam(entry));
  const index = first && typeof first.dataIndex === 'number' ? first.dataIndex : 0;
  const point = item.trajectory[index];
  if (!point) return '';
  const lines = [`<strong>${formatSeconds(point.time_s)}</strong>`];
  lines.push(tooltipSide('Current', comparison ? point.paired_gap?.current : point.current.gap, point.current.objective));
  if (comparison && point.baseline) {
    lines.push(tooltipSide('Baseline', point.paired_gap?.baseline, point.baseline.objective));
  }
  return lines.join('<br/>');
}

function tooltipSide(label: string, gap: Distribution | undefined, objective: Distribution): string {
  return [
    `<span style="font-weight:600">${label}</span>`,
    `Gap: ${formatPercent(gap?.median ?? null)} <span style="color:#69757d">(P25–P75 ${formatPercentRange(gap)})</span>`,
    `Objective: ${formatNumber(objective.median)} <span style="color:#69757d">(P25–P75 ${formatNumberRange(objective)})</span>`,
  ].join('<br/>');
}

function isChartParam(value: unknown): value is { dataIndex: number } {
  return typeof value === 'object' && value !== null && 'dataIndex' in value && typeof value.dataIndex === 'number';
}

function bandSeries(name: string, values: Array<Distribution | undefined>, color: string, stack: string) {
  const lower = values.map((value) => value?.p25 ?? null);
  const spread = values.map((value) => value?.p25 != null && value.p75 != null ? value.p75 - value.p25 : null);
  const medians = values.map((value) => value?.median ?? null);
  return [
    { name: `${name} P25`, type: 'line', data: lower, stack, symbol: 'none', lineStyle: { opacity: 0 }, areaStyle: { opacity: 0 }, tooltip: { show: false } },
    { name: `${name} P25–P75`, type: 'line', data: spread, stack, symbol: 'none', lineStyle: { opacity: 0 }, areaStyle: { color, opacity: 0.12 }, tooltip: { show: false } },
    { name, type: 'line', data: medians, symbol: 'circle', symbolSize: 5, lineStyle: { color, width: 2 }, itemStyle: { color }, connectNulls: false },
  ];
}

function Verification({ passed, status, errors = [] }: { passed: boolean; status: string; errors?: string[] }) {
  return <span title={errors.join('\n') || status || undefined} className={`verification ${passed ? 'passed' : 'failed'}`}>{passed ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}{passed ? '有效' : statusLabel(status)}</span>;
}

function ChangeValue({ value }: { value: number | null }) {
  if (value == null) return <span className="muted">—</span>;
  const change = value < 0 ? 'improved' : value > 0 ? 'regressed' : 'unchanged';
  return <b className={`delta ${change}`}>{value > 0 ? '+' : ''}{value.toFixed(2)} pp</b>;
}

function ObjectiveValue({ value, reference, sense }: { value: number | null; reference: number | null; sense: string }) {
  const delta = objectiveDelta(value, reference, sense);
  const change = delta == null ? 'unchanged' : delta < 0 ? 'improved' : delta > 0 ? 'regressed' : 'unchanged';
  return <span className="objective-value"><b>{formatNumber(value)}</b><small className={`delta ${change}`}>Δ {formatSignedNumber(delta)}</small></span>;
}

function ConfigFact({ label, value }: { label: string; value: string }) { return <div><span>{label}</span><strong>{value}</strong></div>; }
function GuideItem({ title, text }: { title: string; text: string }) { return <div><strong>{title}</strong><span>{text}</span></div>; }
function SectionTitle({ title, detail }: { title: string; detail: string }) { return <div className="section-title"><h1>{title}</h1><span>{detail}</span></div>; }
function FamilyName({ family }: { family: string }) {
  const label = familyLabel(family);
  return <h2 className="family-name"><span>{label}</span>{label !== family && <small>{family}</small>}</h2>;
}
function StatePage({ title, detail, error = false }: { title: string; detail: string; error?: boolean }) { return <main className={`state-page ${error ? 'error' : ''}`}><BarChart3 size={28} /><strong>{title}</strong>{detail && <p>{detail}</p>}</main>; }

function filterFamilies(families: ResultFamily[], query: string): ResultFamily[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return families;
  return families.map((family) => ({ ...family, cases: family.cases.filter((item) => `${family.family} ${familyLabel(family.family)} ${item.case_id}`.toLowerCase().includes(normalized)) })).filter((family) => family.cases.length > 0);
}
function findCase(families: ResultFamily[], caseId: string) { return families.flatMap((family) => family.cases).find((item) => item.case_id === caseId); }
function findFamily(families: ResultFamily[], familyId: string) { return families.find((family) => family.family === familyId); }
function lastItem<T>(items: T[]) { return items.length > 0 ? items[items.length - 1] : undefined; }
function exitLabel(item: ReviewRun['current']['observations'][number] | undefined) { return item?.search_ended ? `${item.termination_reason || '已结束'} @ ${formatSeconds(item.search_ended_at_s || item.time_s)}` : '预算结束'; }
function formatSeconds(value: number) { return `${value.toFixed(0)}s`; }
function formatCompactSeconds(value: number) { return `${Number.isInteger(value) ? value.toFixed(0) : value}s`; }
function formatConfigSeconds(value: number | null) { return value == null ? '—' : formatCompactSeconds(value); }
function formatConfigWindow(value: number | null, range: number[] | undefined) {
  if (value != null) return formatConfigSeconds(value);
  if (range && range.length > 1) return `${formatCompactSeconds(range[0])}–${formatCompactSeconds(range[range.length - 1])}`;
  return '—';
}
function formatConfigNumber(value: number | null) { return value == null ? '—' : String(value); }
function formatNumber(value: number | null) { return value == null ? '—' : Number.isInteger(value) ? value.toFixed(0) : value.toPrecision(6); }
function formatSignedNumber(value: number | null) { return value == null ? '—' : `${value > 0 ? '+' : ''}${formatNumber(value)}`; }
function formatPercent(value: number | null) { return value == null ? '—' : `${value.toFixed(2)}%`; }
function formatRange(value: Distribution) { return value.p25 == null || value.p75 == null ? '—' : `${value.p25.toFixed(2)}–${value.p75.toFixed(2)}`; }
function formatPercentRange(value: Distribution | undefined) { return value?.p25 == null || value.p75 == null ? '—' : `${value.p25.toFixed(2)}–${value.p75.toFixed(2)}%`; }
function formatNumberRange(value: Distribution) { return value.p25 == null || value.p75 == null ? '—' : `${formatNumber(value.p25)}–${formatNumber(value.p75)}`; }
function shortCommit(value: string) { return value === 'unknown' ? '未记录 commit' : value.slice(0, 8); }
function displayStrategy(value: string) { return value.toUpperCase(); }
function roleLabel(value: string) { return value === 'target' ? '目标算例族' : '观察算例族'; }
function senseLabel(value: string) { return value.startsWith('max') ? '最大化' : '最小化'; }
function objectiveDelta(value: number | null, reference: number | null, sense: string) {
  if (value == null || reference == null) return null;
  return sense.startsWith('max') ? reference - value : value - reference;
}
function statusLabel(value: string) { return ({ observation_verification_failed: '快照验证失败', verification_failed: '验证失败', external_cancelled: '外部取消', cancelled: '已取消', infrastructure_interrupted: '基础设施中断', error: '错误', missing: '缺失' } as Record<string, string>)[value] ?? (value || '无效'); }
function familyLabel(value: string) { return ({ interval_job_shop: '作业车间调度', sequence_blackbox_tsp: '旅行商路径', sequence_quadratic_assignment: '二次分配', flexible_interval_job_shop: '柔性作业车间', cumulative_resource_scheduling: '累积资源调度', sequence_transition_penalty: '序列切换成本' } as Record<string, string>)[value] ?? value; }
